import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheckCircle,
  faCopy,
  faDatabase,
  faDownload,
  faTimes,
  faEdit,
  faExclamationTriangle,
  faPalette,
  faSave,
  faShieldAlt,
  faSlidersH,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';

const SystemSettingsPage: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const breadcrumb = [{ label: 'Nastavení' }, { label: 'Systémové nastavení' }];

  const settings = {
    general: {
      salonName: 'Premium Salon',
      timezone: 'Europe/Prague',
      dateFormat: 'DD.MM.YYYY',
      timeFormat: '24h',
      currency: 'CZK',
      language: 'cs',
      autoSave: true,
      notifications: true,
    },
    appearance: {
      theme: 'light',
      primaryColor: '#10B981',
      sidebarCollapsed: false,
      showAnimations: true,
      compactMode: false,
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      newReservations: true,
      cancellations: true,
      reminders: true,
      dailyReports: true,
      weeklyReports: false,
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 30,
      passwordExpiry: 90,
      loginAttempts: 5,
      ipWhitelist: false,
      auditLog: true,
    },
    backup: {
      autoBackup: true,
      backupFrequency: 'daily',
      backupRetention: 30,
      cloudBackup: false,
      lastBackup: '2024-11-15T10:30:00Z',
      nextBackup: '2024-11-16T02:00:00Z',
    },
  };

  const tabs = [
    { id: 'general', name: 'Obecné', icon: faSlidersH },
    { id: 'appearance', name: 'Vzhled', icon: faPalette },
    { id: 'notifications', name: 'Notifikace', icon: faBell },
    { id: 'security', name: 'Bezpečnost', icon: faShieldAlt },
    { id: 'backup', name: 'Zálohy', icon: faDatabase },
  ];

  const languages = [
    { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  ];

  const themes = [
    { id: 'light', name: 'Světlý', preview: 'bg-white border-gray-200' },
    { id: 'dark', name: 'Tmavý', preview: 'bg-gray-800 border-gray-700' },
    { id: 'auto', name: 'Automaticky', preview: 'bg-gradient-to-r from-white to-gray-800' },
  ];

  const currencies = [
    { code: 'CZK', name: 'Česká koruna', symbol: 'Kč' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'Americký dolar', symbol: '$' },
  ];

  const timezones = ['Europe/Prague', 'Europe/Berlin', 'Europe/Vienna', 'Europe/Bratislava', 'UTC'];

  const handleSettingChange = (section: string, key: string, value: any) => {
    console.log(`Changing ${section}.${key} to ${value}`);
  };

  const handleSave = () => {
    console.log('Saving system settings');
    setIsEditing(false);
  };

  const formatLastBackup = (dateString: string) => {
    return new Date(dateString).toLocaleString('cs-CZ');
  };

  const getBackupStatus = () => {
    const lastBackup = new Date(settings.backup.lastBackup);
    const now = new Date();
    const diffHours = (now.getTime() - lastBackup.getTime()) / (1000 * 60 * 60);

    if (diffHours < 24) {
      return { status: 'success', text: 'V pořádku', color: 'text-green-600' };
    } else if (diffHours < 72) {
      return { status: 'warning', text: 'Starší než 24h', color: 'text-yellow-600' };
    } else {
      return { status: 'error', text: 'Starší než 72h', color: 'text-red-600' };
    }
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Název salonu</label>
          {isEditing ? (
            <input
              type="text"
              value={settings.general.salonName}
              onChange={e => handleSettingChange('general', 'salonName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            />
          ) : (
            <p className="text-gray-900">{settings.general.salonName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Časové pásmo</label>
          {isEditing ? (
            <select
              value={settings.general.timezone}
              onChange={e => handleSettingChange('general', 'timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-gray-900">{settings.general.timezone}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Formát data</label>
          {isEditing ? (
            <select
              value={settings.general.dateFormat}
              onChange={e => handleSettingChange('general', 'dateFormat', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            >
              <option value="DD.MM.YYYY">DD.MM.YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          ) : (
            <p className="text-gray-900">{settings.general.dateFormat}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Formát času</label>
          {isEditing ? (
            <select
              value={settings.general.timeFormat}
              onChange={e => handleSettingChange('general', 'timeFormat', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            >
              <option value="24h">24 hodin</option>
              <option value="12h">12 hodin (AM/PM)</option>
            </select>
          ) : (
            <p className="text-gray-900">{settings.general.timeFormat === '24h' ? '24 hodin' : '12 hodin (AM/PM)'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Měna</label>
          {isEditing ? (
            <select
              value={settings.general.currency}
              onChange={e => handleSettingChange('general', 'currency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            >
              {currencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
          ) : (
            <p className="text-gray-900">
              {currencies.find(c => c.code === settings.general.currency)?.name}({currencies.find(c => c.code === settings.general.currency)?.symbol})
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Jazyk</label>
          {isEditing ? (
            <select
              value={settings.general.language}
              onChange={e => handleSettingChange('general', 'language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-gray-900">
              {languages.find(l => l.code === settings.general.language)?.flag}
              {languages.find(l => l.code === settings.general.language)?.name}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Obecné možnosti</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.general.autoSave}
              onChange={e => handleSettingChange('general', 'autoSave', e.target.checked)}
              disabled={!isEditing}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
            />
            <span className="ml-3 text-sm text-gray-700">Automatické ukládání změn</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.general.notifications}
              onChange={e => handleSettingChange('general', 'notifications', e.target.checked)}
              disabled={!isEditing}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
            />
            <span className="ml-3 text-sm text-gray-700">Povolit notifikace</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Téma</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {themes.map(theme => (
            <button
              key={theme.id}
              onClick={() => isEditing && handleSettingChange('appearance', 'theme', theme.id)}
              disabled={!isEditing}
              className={`p-4 rounded-lg border-2 transition-colors ${
                settings.appearance.theme === theme.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
              } ${!isEditing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className={`w-full h-8 rounded mb-2 border ${theme.preview}`}></div>
              <span className="text-sm font-medium text-gray-900">{theme.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Hlavní barva</label>
        {isEditing ? (
          <input
            type="color"
            value={settings.appearance.primaryColor}
            onChange={e => handleSettingChange('appearance', 'primaryColor', e.target.value)}
            className="w-20 h-10 border border-gray-300 rounded cursor-pointer"
          />
        ) : (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded border border-gray-300" style={{ backgroundColor: settings.appearance.primaryColor }}></div>
            <span className="text-gray-900">{settings.appearance.primaryColor}</span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Možnosti zobrazení</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.appearance.sidebarCollapsed}
              onChange={e => handleSettingChange('appearance', 'sidebarCollapsed', e.target.checked)}
              disabled={!isEditing}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
            />
            <span className="ml-3 text-sm text-gray-700">Výchozí stav postranního menu (sbalené)</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.appearance.showAnimations}
              onChange={e => handleSettingChange('appearance', 'showAnimations', e.target.checked)}
              disabled={!isEditing}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
            />
            <span className="ml-3 text-sm text-gray-700">Zobrazit animace</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.appearance.compactMode}
              onChange={e => handleSettingChange('appearance', 'compactMode', e.target.checked)}
              disabled={!isEditing}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
            />
            <span className="ml-3 text-sm text-gray-700">Kompaktní režim</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderNotificationsSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Typy notifikací</label>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.emailNotifications}
                onChange={e => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">E-mail notifikace</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.smsNotifications}
                onChange={e => handleSettingChange('notifications', 'smsNotifications', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">SMS notifikace</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.pushNotifications}
                onChange={e => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">Push notifikace</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Události</label>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.newReservations}
                onChange={e => handleSettingChange('notifications', 'newReservations', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">Nové rezervace</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.cancellations}
                onChange={e => handleSettingChange('notifications', 'cancellations', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">Zrušení rezervací</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.reminders}
                onChange={e => handleSettingChange('notifications', 'reminders', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">Připomínky</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Reporty</label>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.dailyReports}
                onChange={e => handleSettingChange('notifications', 'dailyReports', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">Denní reporty</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.weeklyReports}
                onChange={e => handleSettingChange('notifications', 'weeklyReports', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">Týdenní reporty</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Dvoufaktorová autentizace</label>
          <div className="flex items-center space-x-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.security.twoFactorAuth}
                onChange={e => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">{settings.security.twoFactorAuth ? 'Povoleno' : 'Zakázáno'}</span>
            </label>
            {isEditing && <button className="text-blue-600 hover:text-blue-800 text-sm">Nastavit</button>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Timeout relace (minuty)</label>
          {isEditing ? (
            <input
              type="number"
              value={settings.security.sessionTimeout}
              onChange={e => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            />
          ) : (
            <p className="text-gray-900">{settings.security.sessionTimeout} minut</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Expirace hesla (dny)</label>
          {isEditing ? (
            <input
              type="number"
              value={settings.security.passwordExpiry}
              onChange={e => handleSettingChange('security', 'passwordExpiry', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            />
          ) : (
            <p className="text-gray-900">{settings.security.passwordExpiry} dnů</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Maximální pokusy přihlášení</label>
          {isEditing ? (
            <input
              type="number"
              value={settings.security.loginAttempts}
              onChange={e => handleSettingChange('security', 'loginAttempts', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            />
          ) : (
            <p className="text-gray-900">{settings.security.loginAttempts} pokusů</p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Další bezpečnostní možnosti</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.security.ipWhitelist}
              onChange={e => handleSettingChange('security', 'ipWhitelist', e.target.checked)}
              disabled={!isEditing}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
            />
            <span className="ml-3 text-sm text-gray-700">Whitelist IP adres</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.security.auditLog}
              onChange={e => handleSettingChange('security', 'auditLog', e.target.checked)}
              disabled={!isEditing}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
            />
            <span className="ml-3 text-sm text-gray-700">Auditní log</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderBackupSettings = () => {
    const backupStatus = getBackupStatus();

    return (
      <div className="space-y-6">
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Stav zálohování</h4>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${backupStatus.color}`}>
              <FontAwesomeIcon icon={backupStatus.status === 'success' ? faCheckCircle : faExclamationTriangle} className="w-3 h-3 mr-1" />
              {backupStatus.text}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Poslední záloha:</p>
              <p className="font-medium text-gray-900">{formatLastBackup(settings.backup.lastBackup)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Další záloha:</p>
              <p className="font-medium text-gray-900">{formatLastBackup(settings.backup.nextBackup)}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Automatické zálohování</label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.backup.autoBackup}
                onChange={e => handleSettingChange('backup', 'autoBackup', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">Povolit automatické zálohování</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Frekvence zálohování</label>
            {isEditing ? (
              <select
                value={settings.backup.backupFrequency}
                onChange={e => handleSettingChange('backup', 'backupFrequency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="hourly">Každou hodinu</option>
                <option value="daily">Denně</option>
                <option value="weekly">Týdně</option>
                <option value="monthly">Měsíčně</option>
              </select>
            ) : (
              <p className="text-gray-900">
                {settings.backup.backupFrequency === 'hourly' && 'Každou hodinu'}
                {settings.backup.backupFrequency === 'daily' && 'Denně'}
                {settings.backup.backupFrequency === 'weekly' && 'Týdně'}
                {settings.backup.backupFrequency === 'monthly' && 'Měsíčně'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Uchování záloh (dny)</label>
            {isEditing ? (
              <input
                type="number"
                value={settings.backup.backupRetention}
                onChange={e => handleSettingChange('backup', 'backupRetention', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              />
            ) : (
              <p className="text-gray-900">{settings.backup.backupRetention} dnů</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cloud zálohování</label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.backup.cloudBackup}
                onChange={e => handleSettingChange('backup', 'cloudBackup', e.target.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
              />
              <span className="ml-3 text-sm text-gray-700">Povolit cloud zálohování</span>
            </label>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            <FontAwesomeIcon icon={faCopy} className="mr-2" />
            Vytvořit zálohu nyní
          </button>
          <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Stáhnout zálohu
          </button>
          <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
            <FontAwesomeIcon icon={faUpload} className="mr-2" />
            Obnovit ze zálohy
          </button>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'appearance':
        return renderAppearanceSettings();
      case 'notifications':
        return renderNotificationsSettings();
      case 'security':
        return renderSecuritySettings();
      case 'backup':
        return renderBackupSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Systémové nastavení" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Systémové nastavení</h1>
                    <p className="text-gray-600 mt-1">Spravujte systémová nastavení aplikace</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    {!isEditing ? (
                      <button onClick={() => setIsEditing(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                        <FontAwesomeIcon icon={faEdit} className="mr-2" />
                        Upravit
                      </button>
                    ) : (
                      <>
                        <button onClick={() => setIsEditing(false)} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                          <FontAwesomeIcon icon={faTimes} className="mr-2" />
                          Zrušit
                        </button>
                        <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                          <FontAwesomeIcon icon={faSave} className="mr-2" />
                          Uložit
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.id ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <FontAwesomeIcon icon={tab.icon} className="w-4 h-4 mr-2" />
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">{renderTabContent()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettingsPage;
